/**
 * When this client's mentors are unavailable.
 *
 * *** READ-ONLY, AND THAT IS DELIBERATE ***
 * Availability is the mentor's own statement about when they can work. An
 * admin clearing a break would make that mentor bookable at a time they
 * genuinely cannot attend — so the API offers no write route, and this card
 * offers no controls. It answers "why can nobody book Rahul next week?",
 * which is otherwise unanswerable from the portal.
 */

import { Card, Callout } from '@/components/ui/Page'
import { Table } from '@/components/ui/Table'
import type { Column } from '@/components/ui/Table'
import { Pill } from '@/components/ui/Pill'
import { useMentorBreaks } from '@/lib/queries'
import type { ScheduledBreak } from '@/types/api'

/** A date-only string as a short readable day. */
function formatDay(value: string): string {
  const parsed = new Date(`${value}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/** "14:00:00" as "14:00" — seconds are noise on a calendar. */
function formatClock(value: string | null): string {
  return value ? value.slice(0, 5) : ''
}

function describeWhen(row: ScheduledBreak): string {
  const days =
    row.start_date === row.end_date
      ? formatDay(row.start_date)
      : `${formatDay(row.start_date)} – ${formatDay(row.end_date)}`

  if (!row.start_time) return days
  return `${days} · ${formatClock(row.start_time)}–${formatClock(row.end_time)}`
}

const columns: Array<Column<ScheduledBreak>> = [
  {
    key: 'mentor',
    header: 'Mentor',
    render: (row) => row.mentor_name,
  },
  {
    key: 'kind',
    header: 'Type',
    render: (row) =>
      row.kind === 'VACATION' ? (
        <Pill tone="info">Vacation</Pill>
      ) : (
        <Pill tone="neutral">Break</Pill>
      ),
  },
  {
    key: 'when',
    header: 'When',
    render: (row) => describeWhen(row),
  },
  {
    key: 'days',
    header: 'Days',
    align: 'right',
    // A vacation is ONE entry spanning several days, so the span is worth a
    // column of its own — otherwise "16 Aug – 8 Sep" has to be counted by eye.
    render: (row) => (row.kind === 'VACATION' ? row.days : '—'),
  },
  {
    key: 'label',
    header: 'Note',
    render: (row) => row.label ?? '—',
  },
]

export function MentorBreaksCard() {
  const { data, isLoading, error, refetch } = useMentorBreaks()

  const enabled = data?.enabled ?? false
  const breaks = data?.breaks ?? []

  // Only once loaded: assuming "not enabled" while the request is in flight
  // would flash a misleading explanation on every visit.
  if (data && !enabled) {
    return (
      <Callout tone="info" title="Time off is not enabled for this organisation">
        <p>
          Mentors here cannot book vacations or block out part of a day. Their
          weekly working hours and recurring breaks still apply as normal.
        </p>
        <p>Contact the platform team if this client needs it.</p>
      </Callout>
    )
  }

  return (
    <Card
      flush
      title="Upcoming time off"
      description="When mentors are unavailable. Set by the mentors themselves — this is a view only."
    >
      <Table
        columns={columns}
        rows={breaks}
        rowKey={(row) => `${row.mentor_id}-${row.kind}-${row.start_date}`}
        loading={isLoading}
        error={error}
        onRetry={() => void refetch()}
        emptyMessage="No mentor has upcoming time off."
      />
    </Card>
  )
}
